/**
 * voice-follow.js — a script that follows the reader's voice.
 *
 * Shared between /teleprompter (where the matcher was born, 2026-08) and
 * /webcam-recorder (2026-09-04). The module owns the QUESTION «where in the
 * whole script does what I just heard fit best?»; the tool owns the ANSWER
 * on screen (which pixel to scroll to, what the pill says).
 *
 * WHY WHOLE-SCRIPT ANCHORED ALIGNMENT (measured on /teleprompter,
 * tests/playwright/teleprompter-voice.mjs): the category's known failure is
 * «misheard one word, jumped three paragraphs». A forward-only window can
 * never find a backtrack, and prefix matching («по» ⊂ «помогает») advanced
 * the cursor on every Russian two-letter word. Here anchors are weighted by
 * rarity inside THIS script (idf — a word used once locates, a word used
 * thirty times is nearly noise), the spoken tail is aligned by a suffix DP
 * with free gaps on both sides (ASR drops words, speakers add them), and a
 * move is committed only when the best place clears an absolute score AND
 * beats the runner-up — holding is always safe, jumping is not.
 *
 * Public API (window.VoiceFollow):
 *   isSupported()                       — Web Speech present in this browser
 *   create(opts) → follower
 *     opts.slug        telemetry attribution (required by WebSpeechSession)
 *     opts.lang        BCP-47 for the recogniser (default window.__SPEECH_LANG)
 *     opts.words       Array<string> — the script, one NORMALISED token per
 *                      word (see normalize()); index = word index the tool
 *                      scrolls to
 *     opts.onMove(wordIdx, {isFinal, atEnd})  — position committed
 *     opts.onLock(locked)   — evidence strong (true) / holding (false)
 *     opts.onListening(on)  — recogniser session open / closed
 *     opts.onFatal(reason)  — the lane is gone; fall back to timed scroll
 *     opts.onDebug(dbg)     — per-utterance internals (tests)
 *     opts.autoRestart      — fn or bool, forwarded to WebSpeechSession
 *   follower.start() / stop() / destroy() / setWords(words) / reset()
 *   VoiceFollow.normalize(word) — the tokeniser the tool must use for opts.words
 */
(function () {
    'use strict';
    if (window.VoiceFollow) return;

    var TAIL = 14;          // spoken tokens kept as context
    var ANCHORS = 6;        // strongest tail tokens used to seed candidates
    var SPAN = 12;          // script tokens the tail is aligned against
    var MIN_SCORE = 1.9;    // absolute evidence needed to move at all
    var MARGIN = 1.35;      // a far jump must beat the runner-up by this much
    var MARGIN_NEAR = 1.04; // a local correction only has to win
    var NEAR = 40;          // tokens counted as "continuing where we are"
    var NEAR_BONUS = 1.10;  // tie-break toward local reading, not a veto
    var DECAY = 0.82;       // per-token recency weight inside the tail
    var CLUSTER = 6;        // candidates this close are ONE hypothesis, not rivals
    var CARRY = 3;          // tokens of the previous utterance kept as context

    function normalize(w) { return (w || '').toLowerCase().replace(/ё/g, 'е').replace(/[^\p{L}\p{N}]/gu, ''); }

    /* Two tokens are the same word if they are equal, or share a >=5-char stem
       with a similar length. The stem rule absorbs the case/number endings ASR
       gets wrong in Russian («правило»/«правила») while refusing the short-word
       prefix collisions («по» vs «помогает»). */
    function tokEq(a, b) {
        if (a === b) return true;
        if (a.length < 5 || b.length < 5) return false;
        if (Math.abs(a.length - b.length) > 3) return false;
        var n = Math.min(a.length, b.length), i = 0;
        while (i < n && a.charCodeAt(i) === b.charCodeAt(i)) i++;
        return i >= 5;
    }

    function buildIndex(words) {
        var ix = { pos: Object.create(null), idf: Object.create(null), toks: [], wordIdx: [] };
        for (var i = 0; i < words.length; i++) {
            var t = words[i];
            if (!t || t.length < 3) continue;          // too short to locate anything
            ix.toks.push(t); ix.wordIdx.push(i);
            (ix.pos[t] || (ix.pos[t] = [])).push(ix.toks.length - 1);
        }
        var N = ix.toks.length || 1;
        for (var k in ix.pos) ix.idf[k] = Math.log(1 + N / ix.pos[k].length);
        return ix;
    }

    function create(opts) {
        opts = opts || {};
        if (!opts.slug) throw new Error('VoiceFollow: opts.slug is required');
        var noop = function () {};
        var onMove = opts.onMove || noop, onLock = opts.onLock || noop, onListening = opts.onListening || noop;
        var onFatal = opts.onFatal || noop, onDebug = opts.onDebug || noop;
        var IX = buildIndex(opts.words || []);
        var cur = 0, tail = [], carry = [], session = null, locked = null;

        function idfOf(t) {
            if (IX.idf[t] !== undefined) return IX.idf[t];
            return t.length >= 6 ? 0.9 : 0.4;      // ad-lib / ASR noise: worth a little, never much
        }
        /* Suffix DP: align the spoken tail ending "now" against the script tokens
           ending at `end`. Gaps are free on both sides and bounded by the window;
           matches are weighted idf × recency. */
        function alignScore(t, end) {
            var m = Math.min(t.length, SPAN), n = Math.min(end + 1, SPAN);
            if (m <= 0 || n <= 0) return 0;
            var prev = new Float64Array(n + 1), curr = new Float64Array(n + 1);
            for (var a2 = m - 1; a2 >= 0; a2--) {
                var ta = t[t.length - 1 - a2], wgt = Math.pow(DECAY, a2);
                curr[n] = 0;
                for (var b2 = n - 1; b2 >= 0; b2--) {
                    var gb = IX.toks[end - b2];
                    var hit = tokEq(gb, ta) ? idfOf(gb) * wgt + prev[b2 + 1] : 0;
                    var skipTail = prev[b2], skipScript = curr[b2 + 1];
                    curr[b2] = hit > skipTail ? (hit > skipScript ? hit : skipScript) : (skipTail > skipScript ? skipTail : skipScript);
                }
                var tmp = prev; prev = curr; curr = tmp;
            }
            return prev[0];
        }
        function setLock(on) { if (locked === on) return; locked = on; onLock(on); }

        function feed(spoken, isFinal) {
            if (!IX.toks.length) return;
            var fresh = [];
            for (var i = 0; i < spoken.length; i++) { var t = normalize(spoken[i]); if (t.length >= 2) fresh.push(t); }
            if (!fresh.length) return;
            // Tail = a little context + THIS utterance; a finished sentence must not
            // keep voting for the neighbourhood it just left.
            tail = carry.concat(fresh);
            while (tail.length > TAIL) tail.shift();
            if (isFinal) carry = fresh.slice(-CARRY);

            var seeds = [];
            for (var s2 = tail.length - 1; s2 >= 0 && seeds.length < ANCHORS; s2--) { var tk = tail[s2]; if (tk.length >= 3) seeds.push(tk); }
            var cand = Object.create(null);
            for (var q = 0; q < seeds.length; q++) {
                var lst = IX.pos[seeds[q]];
                if (lst && lst.length <= 60) for (var z = 0; z < lst.length; z++) cand[lst[z]] = 1;
                if (!lst) { for (var y = 0; y < IX.toks.length; y++) if (tokEq(IX.toks[y], seeds[q])) cand[y] = 1; }
            }
            var scored = [];
            for (var c in cand) {
                var e = +c, sc = alignScore(tail, e);
                if (!sc) continue;
                if (Math.abs(e - cur) <= NEAR) sc *= NEAR_BONUS;
                scored.push([e, sc]);
            }
            var best = -1, bestScore = 0, second = 0;
            for (var i2 = 0; i2 < scored.length; i2++) if (scored[i2][1] > bestScore) { bestScore = scored[i2][1]; best = scored[i2][0]; }
            for (var i3 = 0; i3 < scored.length; i3++) {
                if (Math.abs(scored[i3][0] - best) <= CLUSTER) continue;
                if (scored[i3][1] > second) second = scored[i3][1];
            }
            var need = Math.abs(best - cur) <= NEAR ? MARGIN_NEAR : MARGIN;
            var confident = best >= 0 && bestScore >= MIN_SCORE && bestScore >= second * need;
            onDebug({ tail: tail.slice(), cur: cur, best: best, bestScore: bestScore, second: second, confident: confident, nCand: Object.keys(cand).length });
            setLock(confident);
            if (!confident) return;   // ad-lib / noise / ambiguity → hold, never guess
            cur = best;
            onMove(IX.wordIdx[best], { isFinal: !!isFinal, atEnd: !!isFinal && best >= IX.toks.length - 1 });
        }

        var follower = {
            feed: feed,
            setWords: function (words) { IX = buildIndex(words || []); cur = 0; tail = []; carry = []; },
            reset: function () { cur = 0; tail = []; carry = []; locked = null; },
            start: function () {
                if (!window.WebSpeechSession || !WebSpeechSession.isSupported()) { onFatal('unsupported'); return false; }
                if (session) { try { session.destroy(); } catch (_) {} }
                session = new WebSpeechSession({
                    slug: opts.slug,
                    lang: opts.lang || window.__SPEECH_LANG,
                    continuous: true,
                    interimResults: true,
                    autoRestart: opts.autoRestart === undefined ? true : opts.autoRestart,
                    onResult: function (r) {
                        for (var i = r.resultIndex; i < r.results.length; i++) {
                            var transcript = (r.results[i][0].transcript || '').trim().toLowerCase();
                            feed(transcript.split(/\s+/), r.results[i].isFinal);
                        }
                    },
                    onStart: function () { onListening(true); },
                    onEnd: function () { onListening(false); },
                    onFatal: function (reason) { onListening(false); onFatal(reason); }
                });
                session.start();
                return true;
            },
            stop: function () { if (session) { try { session.stop(); } catch (_) {} } },
            destroy: function () { if (session) { try { session.destroy(); } catch (_) {} session = null; } },
            get session() { return session; }
        };
        return follower;
    }

    window.VoiceFollow = {
        isSupported: function () { return !!(window.WebSpeechSession && WebSpeechSession.isSupported()); },
        create: create,
        normalize: normalize,
        _tokEq: tokEq
    };
})();
